import {
  Button,
  EllipsisSpan,
  FieldTable,
  Form,
  FormContent,
  Radio,
  Tag,
  TreeSelect,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { Col, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import Edit from './TableEdit';
import BindUnitModal from '../BindUnitModal';

import {
  apiV2EnergyOverviewMediumByEnergyUnitPost,
  apiV2AreaAddConfigPost,
  apiV2AreaUpdateConfigPost,
  V2AreaAddConfigPostRequest,
  apiV2AreaTreePost,
  apiV2AreaGetConfigDetailPost,
  V2AreaGetConfigDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { transformToTreeSelectOptions } from '@/pages/AreaAdministration/utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export type EnergyItem = V2AreaGetConfigDetailPostResponse;
export type RecordItem = NonNullable<V2AreaGetConfigDetailPostResponse['timePeriodList']>[number];
const AreaConfigDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [showEnergyUnitModal, setShowEnergyUnitModal] = useState<boolean>(false);
  const [unitId, setUnitId] = useState<number>();
  const [unitName, setUnitName] = useState<string>();
  const [editingItem, setEditingItem] = useState<RecordItem>();
  const [form] = Form.useForm();
  const areaName = Form.useWatch('areaName', form) || '';
  const optionItems = Form.useWatch('timePeriodList', form) || [];
  // const unitId = Form.useWatch('energyUnitName', form)
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [showTag, setShowTag] = useState(false);
  const [option, setOption] = useState<any>([]);
  const builedColumns = [
    {
      title: '区域能源介质',
      width: 160,
      dataIndex: 'mediumName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '生产时段',
      width: 200,
      dataIndex: 'timePeriodType',
      render: (_: any, record: RecordItem) => {
        if (!record.timePeriodType) return '-';
        return record.timePeriodType === 1 ? record.timePeriodDetailList?.[0]?.name : '来源用能台账时间';
      },
    },
    ...(isView
      ? []
      : [
          {
            title: '操作',
            width: 80,
            render: (_: any, record: RecordItem) => (
              <Button type="link" onClick={() => handleEdit(record)}>
                编辑
              </Button>
            ),
          },
        ]),
  ];

  //回填
  useEffect(() => {
    if (id) {
      apiV2AreaGetConfigDetailPost({ id }).then(res => {
        form.setFieldsValue({
          areaName: res.areaId,
          energyUnitName: res.energyUnitName,
          isNeedMeterReading: res.isNeedMeterReading,
        });
        setUnitId(res.energyUnitId);
        setUnitName(res.energyUnitName);
        setShowTag(true);
        const optionItems = (res.timePeriodList || []).map(item => ({
          ...item,
          timePeriodType: item.timePeriodType ?? 1,
          timePeriodDetailList: item.timePeriodDetailList || [],
        }));

        form.setFieldsValue({ timePeriodList: optionItems });
      });
    }
  }, []);

  //获取区域列表
  useEffect(() => {
    apiV2AreaTreePost({}).then(data => {
      const dataList = data.list || [];
      const options = transformToTreeSelectOptions(dataList);
      setOption(options);
    });
  }, []);

  useEffect(() => {
    form.setFieldValue('energyUnitName', unitId);
  }, [unitId]);

  // 根据用能单元获取介质选项
  useEffect(() => {
    if (unitId == null) {
      return;
    }
    if (isView) {
      return;
    }
    apiV2EnergyOverviewMediumByEnergyUnitPost({
      list: [unitId],
    }).then(res => {
      const newItems =
        res.list?.map(item => ({
          mediumId: item.id,
          mediumName: item.energyName,
          timePeriodType: undefined,
        })) || [];

      const configMap = new Map();
      optionItems.forEach((item: RecordItem) => {
        if (item.mediumId) {
          configMap.set(item.mediumId, {
            timePeriodType: item.timePeriodType,
            timePeriodDetailList: item.timePeriodDetailList || [],
          });
        }
      });

      const mergedItems = newItems.map(item => {
        const existingConfig = configMap.get(item.mediumId);
        if (existingConfig) {
          return {
            ...item,
            timePeriodType: existingConfig.timePeriodType,
            timePeriodDetailList: existingConfig.timePeriodDetailList,
          };
        }
        return {
          ...item,
          timePeriodDetailList: [],
        };
      });
      form.setFieldsValue({ timePeriodList: mergedItems });
    });
  }, [unitId, isView]);

  const handleTreeSelect = (info: any) => {
    setShowTag(true);
    setUnitId(info.key);
    setUnitName(info.label);
  };

  const handleEdit = (item: RecordItem) => {
    setEditingItem(item);
    setShowDetail(true);
  };

  const handleSave = (updatedItem: RecordItem) => {
    const currentItems = [...optionItems];
    const index = currentItems.findIndex(i => i.mediumId === updatedItem?.mediumId);
    if (index !== -1) {
      currentItems[index] = updatedItem;
      form.setFieldsValue({ timePeriodList: currentItems });
    }
  };

  const onFinish = (values: EnergyItem) => {
    const payload: V2AreaAddConfigPostRequest = {
      areaId: values.areaName,
      energyUnitId: unitId!,
      optionItems: values.timePeriodList?.map((item: RecordItem) => ({
        mediumId: item.mediumId!,
        timePeriodType: item.timePeriodType,
        idList: item.timePeriodDetailList?.map(item => item.id).filter((id): id is number => id !== undefined),
      })),
      isNeedMeterReading: values.isNeedMeterReading,
    };

    if (isEdit) {
      apiV2AreaUpdateConfigPost(payload).then(() => {
        navigate('/area/config/page');
      });
    } else {
      apiV2AreaAddConfigPost(payload).then(() => {
        navigate('/area/config/page');
      });
    }
  };

  return (
    <Wrapper
      routes={[
        ...(breadcrumbRoutes?.routes ?? []),
        { name: isEdit ? '编辑区域配置' : isView ? '查看区域配置' : '新增区域配置' },
      ]}
    >
      <Form
        form={form}
        disabled={isView}
        onFinish={onFinish}
        labelCol={{
          style: {
            whiteSpace: 'nowrap',
            wordBreak: 'keep-all',
          },
        }}
      >
        <FormContent narrow title={isEdit ? '编辑区域配置' : isView ? '查看区域配置' : '新增区域配置'}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="areaName" label="区域" rules={[{ required: true, message: '请选择区域' }]}>
                <TreeSelect
                  placeholder="请选择区域"
                  treeData={option}
                  disabled={isView}
                  showSearch
                  filterTreeNode={(search, node) => {
                    const searchText = (node.title ?? '') as string;
                    return searchText.toLowerCase().includes(search.toLowerCase());
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="isNeedMeterReading"
                label="区域是否需要抄表"
                rules={[{ required: true, message: '请选择' }]}
                style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all', flex: 'none' }}
                initialValue={0}
              >
                <Radio.Group>
                  <Radio value={1}>是</Radio>
                  <Radio value={0}>否</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={10}>
              <Form.Item label="关联用能单元" name="energyUnitName" rules={[{ required: true, message: '请选择区域' }]}>
                <div>
                  <Button
                    type="link"
                    onClick={() => {
                      setShowEnergyUnitModal(true);
                    }}
                    style={{ padding: 0, marginRight: 8 }}
                    disabled={isView}
                  >
                    配置
                  </Button>

                  {showTag && (
                    <Tag
                      closeIcon={!isView}
                      onClose={
                        isView
                          ? undefined
                          : () => {
                              setShowTag(false);
                              setUnitId(undefined);
                              setUnitName(undefined);
                              form.setFieldValue('timePeriodList', []);
                            }
                      }
                    >
                      {unitName}
                    </Tag>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Col span={12}>
            <Form.Item name="timePeriodList">
              <div style={{ paddingBottom: 8 }}>生产时段配置/非生产时段配置</div>
              <FieldTable
                columns={builedColumns}
                rowKey="mediumId"
                pagination={false}
                dataSource={optionItems}
                bordered
              />
            </Form.Item>
          </Col>
          {!isView && (
            <Space size="small">
              <Button type="primary" onClick={() => form.submit()}>
                保存
              </Button>
              <Button type="default" onClick={() => navigate('/area/config/page')}>
                取消
              </Button>
            </Space>
          )}
        </FormContent>
      </Form>
      {isView && (
        <Button type="primary" onClick={() => navigate('/area/config/page')}>
          返回
        </Button>
      )}
      {showDetail && (
        <Edit closeModal={() => setShowDetail(false)} item={editingItem!} onSave={handleSave} areaId={areaName!} />
      )}
      {showEnergyUnitModal && (
        <BindUnitModal setOpen={setShowEnergyUnitModal} onUnitIdSelect={handleTreeSelect} energyUnitIds={unitId} />
      )}
    </Wrapper>
  );
};
export default AreaConfigDetail;
